/**
 * Telemetry timeline — staff console debug surface for the
 * structured-event stream the iOS app POSTs to ``/telemetry/events``.
 *
 * Use cases:
 * - "What happened on dad's phone in the last hour?" — filter by
 *   user_id, see chronological events.
 * - "Did anyone hit a recording.interrupt today?" — filter by
 *   event_name prefix ``recording.``.
 * - "Are upload failures clustering?" — filter by ``upload.fail``,
 *   eyeball the rate.
 *
 * Read-only. Pencilout-admin only — contractor-side users shouldn't
 * see each other's device fingerprints.
 */
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ApiError, apiClient } from "@/api/client";
import { useQuery } from "@tanstack/react-query";

interface TelemetryEvent {
  id: string;
  organization_id: string | null;
  organization_name: string | null;
  user_id: string | null;
  user_display_name: string | null;
  user_email: string | null;
  event_name: string;
  metadata: Record<string, unknown>;
  app_version: string | null;
  os_version: string | null;
  device_model: string | null;
  network_type: string | null;
  thermal_state: string | null;
  occurred_at: string;
  received_at: string;
}

interface TelemetryListResponse {
  events: TelemetryEvent[];
}

export function TelemetryPage() {
  const [params, setParams] = useSearchParams();
  const eventNameFilter = params.get("event") ?? "";
  const userFilter = params.get("user") ?? "";
  const orgFilter = params.get("org") ?? "";
  const limitFilter = Number(params.get("limit") ?? 200);

  const [eventDraft, setEventDraft] = useState(eventNameFilter);
  const [userDraft, setUserDraft] = useState(userFilter);
  const [orgDraft, setOrgDraft] = useState(orgFilter);

  const query = useQuery({
    queryKey: ["telemetry-events", eventNameFilter, userFilter, orgFilter, limitFilter],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (eventNameFilter) qs.set("event_name", eventNameFilter);
      if (userFilter) qs.set("user_id", userFilter);
      if (orgFilter) qs.set("organization_id", orgFilter);
      qs.set("limit", String(Math.min(Math.max(limitFilter, 1), 500)));
      return apiClient.get<TelemetryListResponse>(`/telemetry/events?${qs.toString()}`);
    },
    refetchInterval: 30_000,
  });

  const apply = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams();
    if (eventDraft) next.set("event", eventDraft);
    if (userDraft) next.set("user", userDraft);
    if (orgDraft) next.set("org", orgDraft);
    next.set("limit", String(limitFilter));
    setParams(next);
  };

  const clear = () => {
    setEventDraft("");
    setUserDraft("");
    setOrgDraft("");
    setParams(new URLSearchParams({ limit: String(limitFilter) }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-mono text-2xl font-bold tracking-tight">Telemetry</h1>
        <p className="mt-1 max-w-3xl text-sm text-graphite-soft">
          Live stream of structured events from the iOS app — recording lifecycle, upload lifecycle,
          API errors, screen views. Newest first. Auto-refreshes every 30 s.
        </p>
      </div>

      <form
        onSubmit={apply}
        className="flex flex-wrap items-end gap-3 rounded-lg border border-rule bg-paper-warm p-3"
      >
        <div className="min-w-[200px] flex-1">
          <label className="label-mono mb-1 block" htmlFor="telemetry-filter-event">
            Event name (use trailing dot for prefix match)
          </label>
          <input
            id="telemetry-filter-event"
            type="text"
            value={eventDraft}
            onChange={(e) => setEventDraft(e.target.value)}
            placeholder="recording.   or   upload.fail"
            className="w-full rounded border border-rule bg-paper px-2 py-1 font-mono text-sm focus:border-graphite focus:outline-none"
          />
        </div>
        <div className="min-w-[260px] flex-1">
          <label className="label-mono mb-1 block" htmlFor="telemetry-filter-user">
            User ID
          </label>
          <input
            id="telemetry-filter-user"
            type="text"
            value={userDraft}
            onChange={(e) => setUserDraft(e.target.value)}
            placeholder="uuid…"
            className="w-full rounded border border-rule bg-paper px-2 py-1 font-mono text-xs focus:border-graphite focus:outline-none"
          />
        </div>
        <div className="min-w-[260px] flex-1">
          <label className="label-mono mb-1 block" htmlFor="telemetry-filter-org">
            Organization ID
          </label>
          <input
            id="telemetry-filter-org"
            type="text"
            value={orgDraft}
            onChange={(e) => setOrgDraft(e.target.value)}
            placeholder="uuid…"
            className="w-full rounded border border-rule bg-paper px-2 py-1 font-mono text-xs focus:border-graphite focus:outline-none"
          />
        </div>
        <button
          type="submit"
          className="rounded bg-graphite px-3 py-1.5 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft"
        >
          Apply
        </button>
        <button
          type="button"
          onClick={clear}
          className="rounded border border-rule bg-paper px-3 py-1.5 font-mono text-sm font-semibold text-graphite-soft transition hover:bg-paper-warm hover:text-graphite"
        >
          Clear
        </button>
      </form>

      {query.isLoading && <p className="text-sm text-graphite-soft">Loading events…</p>}

      {query.error && (
        <p className="text-sm text-red-pencil">
          {query.error instanceof ApiError ? query.error.detail : "Could not load telemetry."}
        </p>
      )}

      {query.data && (
        <div className="space-y-1">
          <p className="font-mono text-xs text-graphite-soft">
            {query.data.events.length} event
            {query.data.events.length === 1 ? "" : "s"}
            {query.data.events.length === limitFilter &&
              "(showing limit; bump in URL ``?limit=`` for more)"}
          </p>
          <div className="overflow-hidden rounded-lg border border-rule bg-paper-warm">
            <table className="w-full">
              <thead className="border-b border-rule bg-paper">
                <tr>
                  <th className="label-mono px-3 py-2 text-left">Time</th>
                  <th className="label-mono px-3 py-2 text-left">Event</th>
                  <th className="label-mono px-3 py-2 text-left">User</th>
                  <th className="label-mono px-3 py-2 text-left">Device</th>
                  <th className="label-mono px-3 py-2 text-left">Thermal</th>
                  <th className="label-mono px-3 py-2 text-left">Metadata</th>
                </tr>
              </thead>
              <tbody>
                {query.data.events.map((evt) => (
                  <TelemetryRow key={evt.id} event={evt} />
                ))}
              </tbody>
            </table>
            {query.data.events.length === 0 && (
              <div className="p-6 text-center text-sm text-graphite-soft">
                No events match the current filters.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TelemetryRow({ event }: { event: TelemetryEvent }) {
  const occurred = new Date(event.occurred_at);
  const tone = severityTone(event.event_name);
  return (
    <tr className="border-b border-rule/60 align-top last:border-b-0">
      <td className="px-3 py-2 font-mono text-xs text-graphite-soft">
        {occurred.toLocaleTimeString()}
        <div className="text-[10px] text-graphite-faint">{occurred.toLocaleDateString()}</div>
      </td>
      <td className="px-3 py-2 font-mono text-xs">
        <span className={`inline-block rounded px-1.5 py-0.5 ${tone}`}>{event.event_name}</span>
      </td>
      <td className="px-3 py-2 text-[11px] text-graphite-soft">
        {event.user_id ? (
          <>
            <div className="font-mono text-graphite">
              {event.user_display_name ?? "(unknown)"}
            </div>
            {event.user_email && (
              <div className="font-mono text-[10px] text-graphite-faint">
                {event.user_email}
              </div>
            )}
            <div className="font-mono text-[10px] text-graphite-faint">
              {short(event.user_id)}
              {event.organization_name && (
                <> · {event.organization_name}</>
              )}
            </div>
          </>
        ) : (
          <span className="font-mono">— anon</span>
        )}
      </td>
      <td className="px-3 py-2 font-mono text-[11px] text-graphite-soft">
        {event.device_model ?? "?"}
        <div className="text-[10px] text-graphite-faint">
          {event.os_version ?? ""} · {event.app_version ?? ""}
        </div>
      </td>
      <td className="px-3 py-2 font-mono text-[11px]">
        <ThermalCell state={event.thermal_state} />
      </td>
      <td className="px-3 py-2">
        <pre className="overflow-x-auto whitespace-pre-wrap break-words font-mono text-[11px] text-graphite">
          {JSON.stringify(event.metadata, null, 2)}
        </pre>
      </td>
    </tr>
  );
}

function ThermalCell({ state }: { state: string | null }) {
  if (!state) return <span className="text-graphite-faint">—</span>;
  const tone =
    state === "critical"
      ? "text-red-pencil"
      : state === "serious"
        ? "text-burnt-orange"
        : state === "fair"
          ? "text-graphite-soft"
          : "text-graphite-soft";
  return <span className={tone}>{state}</span>;
}

function short(id: string) {
  return id.slice(0, 8);
}

/** Tint the event-name pill by severity bucket — error / fail /
 *  interrupt all read red, recording.start / upload.start are
 *  neutral, anything else is graphite. */
function severityTone(name: string): string {
  if (
    name.includes("error") ||
    name.includes("fail") ||
    name.includes("interrupt") ||
    name === "recording.refused"
  ) {
    return "bg-red-pencil/10 text-red-pencil";
  }
  if (name.startsWith("upload.")) {
    return "bg-blue-100 text-blue-900";
  }
  if (name.startsWith("recording.")) {
    return "bg-yellow-100 text-yellow-900";
  }
  if (name.startsWith("api.")) {
    return "bg-orange-100 text-orange-900";
  }
  return "bg-graphite/10 text-graphite";
}
